import { useTranslation } from "react-i18next";
import {
  Grid,
  Button,
  Skeleton,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { NoInfoComponent } from "../../../components/common/NoInfoImage/noInfoComponent";
import { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ModeEditOutline from "@mui/icons-material/ModeEditOutline";

import { useNavigate, useParams } from "react-router-dom";
import { getUser, getUserStatus } from "../../userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../../consts/codes";
import PeriodicReportState from "../../../components/common/DynamicReport/periodicReportState";
import { ModalDecision } from "../../../components/common/Modals/modalDecision";
import EditNrp from "../../../components/common/infonavit/editNrp/editNrp";
import CustomTablePagination from "../../../components/DataTable/CustomTablePagination";
import {
  GetCredentialNrps,
  DeleteNrp,
} from "../../../services/user/infonavit/adminNrps";

export const AdminNRPs = () => {
  const { rfc } = useParams();

  /**
   * Use Selector
   */
  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  /**
   * use State
   */
  // Data
  const [data, setData] = useState([]);
  const [nrpData, setNrpData] = useState({});
  const [nrpList, setNrpList] = useState([]);

  // Estados
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  // Modales
  const [openEditNrp, setOpenEditNrp] = useState(false);
  const [openDeleteNrp, setOpenDeleteNrp] = useState(false);

  const { t } = useTranslation();

  /**
   * Use Navigate
   */
  const navigate = useNavigate();

  const handleEditNRP = (info) => {
    setNrpData(info);
    setOpenEditNrp(true);
  };

  /**
   * Configuraciones para la tabla
   */
  const columns = [
    {
      name: "rfc",
      label: t("App.infonavitEmployerRecords.table.rfc"),
      options: {
        filter: false,
        sort: true,
        searchable: false,
      },
    },
    {
      name: "nrp",
      label: t("App.infonavitEmployerRecords.table.employerRecords"),
      options: {
        filter: false,
        sort: false,
        searchable: true,
        customHeadLabelRender: () => (
          <p>
            {t("App.infonavitEmployerRecords.table.employerRecords1")}
            <br />
            {t("App.infonavitEmployerRecords.table.employerRecords2")}
          </p>
        ),
      },
    },
    {
      name: "edit",
      label: t("App.infonavitEmployerRecords.table.edit"),
      options: {
        filter: false,
        sort: true,
        searchable: false,
        customBodyRender: (_, info) => (
          <IconButton
            onClick={() => {
              handleEditNRP(data[info.rowIndex]);
            }}
          >
            <ModeEditOutline fontSize={"24px"} style={{ color: "#543ab4" }} />
          </IconButton>
        ),
      },
    },
    {
      name: "delete",
      label: t("App.infonavitEmployerRecords.table.delete"),
      options: {
        filter: false,
        sort: true,
        searchable: false,
        customBodyRender: (_, info) => (
          <IconButton
            onClick={() => {
              setNrpList([data[info.rowIndex]]);
              setOpenDeleteNrp(true);
            }}
          >
            <DeleteOutlineIcon fontSize={"24px"} style={{ color: "#543ab4" }} />
          </IconButton>
        ),
      },
    },
    {
      name: "state",
      label: t("App.infonavitEmployerRecords.table.state"),
      options: {
        filter: false,
        sort: true,
        searchable: false,
        customBodyRender: (value) => (
          <PeriodicReportState
            value={value ? "activo" : "inactivo"}
            condensed
          />
        ),
      },
    },
  ];

  const options = {
    filterType: "dropdown",
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectableRows: "multiple",
    selectToolbarPlacement: "none",
    responsive: "standard",
    searchPlaceholder: t(
      "App.infonavitEmployerRecords.table.filterPlaceholder"
    ),
    onRowSelectionChange: (
      currentRowsSelected,
      allRowsSelected,
      rowsSelected
    ) => {
      setNrpList(rowsSelected.map((index) => data[index]));
    },
    elevation: 0,
    viewColumns: false,
    searchOpen: true,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: isLoadingData ? (
          <Skeleton />
        ) : (
          <NoInfoComponent type="dynamic" text="" />
        ),
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
  };

  /**
   * Use Effect
   */
  /**
   * Carga la información de la tabla
   */
  useEffect(() => {
    if (userStatus === "succeeded") fetchData();
  }, [userStatus]);

  /**
   * Consulta y settea la información de la tabla
   */
  const fetchData = async () => {
    try {
      setData([]);
      setIsLoadingData(true);

      const mainUser = userInfo.mainUser;
      const {
        status,
        data: { responseStatus, response },
      } = await GetCredentialNrps(mainUser, rfc);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        setData(response);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Elimina un NRP
   */
  const handleDeleteNRP = async () => {
    try {
      setOpenDeleteNrp(false);
      setIsLoadingRequest(true);

      const {
        status,
        data: { responseStatus, response },
      } = await DeleteNrp(nrpList);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        await fetchData();
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingRequest(false);
    }
  };

  const handleBackButton = () => {
    navigate("/user/configuration/credentials");
  };

  return (
    <Grid
      container
      gap="3rem"
      paddingBottom="2rem !important"
      padding={{
        xs: "1.5rem",
        sm: "2.25rem 3rem",
        md: "2.25rem 3rem",
        lg: "2.6rem 4.5rem",
        xl: "4.5rem",
      }}
    >
      <Grid item xs="12">
        <p className="display-large__primary-one">
          {t("App.infonavitEmployerRecords.title")}
        </p>
      </Grid>

      <Grid item xs="12">
        <Grid container direction="row" columnSpacing={2}>
          <Grid item>
            <IconButton
              className="btn__filled__blue"
              disableRipple
              onClick={handleBackButton}
              sx={{ borderRadius: "4px" }}
            >
              <ArrowBackIcon fontSize="medium" />
            </IconButton>
          </Grid>
          <Grid item className="col-start">
            <p className="display-small__moderate-blue">
              {t("App.infonavitEmployerRecords.goBack")}
            </p>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs="12">
        <Grid className="companies_info_container">
          <Grid item>
            <p
              style={{ fontWeight: "500" }}
              className="display-medium__primary-one"
            >
              {t("App.infonavitEmployerRecords.subtitle")}
              &nbsp;
              <span
                style={{ fontWeight: "500" }}
                className="display-medium__text-default"
              >
                {data[0]?.companyName ?? ""}
              </span>
            </p>
          </Grid>

          <Grid item>
            <MUIDataTable
              className="dataTable__base datatable-style dataTable--modern"
              style={{ display: "none" }}
              components={{
                TableFooter: (props) => (
                  <CustomTablePagination paginationProps={props} />
                ),
              }}
              data={data}
              columns={columns}
              options={options}
            />
          </Grid>

          <Grid item>
            <Grid container gap="2rem" className="col-flex" paddingTop="1rem">
              {/* <Grid item>
                <Button
                  type="submit"
                  className="btn__filled__blue"
                  disabled={isLoadingRequest}
                >
                  {!isLoadingRequest ? (
                    t("App.buttonLabels.save")
                  ) : (
                    <CircularProgress size={21} color="inherit" />
                  )}
                </Button>
              </Grid> */}

              <Grid item>
                <Button
                  className="btn__outlined__blue"
                  onClick={() => {
                    setOpenDeleteNrp(true);
                  }}
                  disabled={isLoadingRequest}
                >
                  {!isLoadingRequest ? (
                    t("App.buttonLabels.delete")
                  ) : (
                    <CircularProgress size={21} color="inherit" />
                  )}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <ModalDecision
        isOpen={openDeleteNrp}
        setIsOpen={setOpenDeleteNrp}
        message={
          nrpList.length <= 1
            ? t("App.infonavitEmployerRecords.modal.confirmDeleteModal", {
                name: nrpList[0]?.nrp,
              })
            : t("App.infonavitEmployerRecords.modal.confirmDeleteMultipleModal")
        }
        handleAgree={handleDeleteNRP}
        handleDisagree={() => setOpenDeleteNrp(false)}
        title={t("App.infonavitEmployerRecords.modal.titleDeleteModal")}
        agreeText={t("App.infonavitEmployerRecords.modal.yes")}
        disagreeText={t("App.infonavitEmployerRecords.modal.no")}
      />

      <EditNrp
        handleFetch={fetchData}
        openEditNrp={openEditNrp}
        setOpenEditNrp={setOpenEditNrp}
        setNrpData={setNrpData}
        nrpData={nrpData}
      />
    </Grid>
  );
};
